import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import generatePDF, { Options } from "react-to-pdf";
import { authClient } from "../utils/amplifyClient";
import { getUrl } from "aws-amplify/storage";
import {
    Box,
    Button,
    ColumnLayout,
    Container,
    Header,
    Icon,
    Modal,
    StatusIndicator,
    Table,
} from './legacy-widgets';

import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import * as Sentry from "@sentry/browser";
import { DisplayStatusIcon } from "./DisplayStatusIcon";
import { ValueWithLabel } from "./ValueWithLabel";
import {
  getReport as getReportQuery,
  getInspectionReport as getInspectionReportQuery,
  getDailyServiceReport as getDailyServiceReportQuery,
  getShiftReport as getShiftReportQuery,
  listDiscrepancyByEquipmentIdAndUpdatedAt as discrepanciesByEquipmentIdQuery,
  listDiscrepancyByUnitIdAndUpdatedAt as discrepanciesByUnitIdQuery,
} from "../graphql/queries";
import { Report } from "../types/schema";
import {
    DEFAULT_FORM_INPUT_TYPES,
    DEFAULT_REPORT_LABELS,
    ITEM_STATUS_CODES,
    LOCAL_STORAGE_KEYS,
    KEYS_START_OF_DAY_REPORT,
} from '../utils/constants';
import { getTypeFromFileName, isNumeric } from '../utils/helperFunctions';
import { resolveStorageImageUrl } from '../utils/storageImageUtils';
import { displayUnitOfMeasure } from '../utils/unitOfMeasureUtils';
import { PDF_OPTIONS } from '../utils/constants';
import { reportPathSegmentToSource } from '../utils/reportUrlUtils';
import {
  resolveUMCounterFromDailyServiceReport,
  resolveUMCounterFromInspectionReport,
} from '../utils/reportUtils';
import { tFile, tItems } from '../types';
import { useUserContext } from '../contexts/UserContext';
import { useAppContext } from '../contexts/AppContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { BackButton, Btn, PageHeader, Stack } from './ui';
import { IconDownload, IconPrint } from '@icons';
import { InspectionFlowReportView } from './inspection-flow/InspectionFlowReportView';

const reportFromLS = localStorage.getItem(LOCAL_STORAGE_KEYS.REPORT) ? JSON.parse(
  localStorage.getItem(LOCAL_STORAGE_KEYS.REPORT) || '') : null;

type tReportData = Report & { umAtReport?: number | null };

type tIssueLog = {
  name: string;
  notes: string;
  date: Date;
}

type tIssuesReport = {
  status: string,
  initialStatus: string,
  questionIndex?: number | null,
  log: tIssueLog[]
}

// interface InspectionReportProps {
//   reportIdforSA: string | null;
//   onBack?: () => void;
// }

// TODO: figure out why pdf isn't capturing media
// TODO: clean up code

export const InspectionReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [renderPhotosModal, setRenderPhotosModal] = useState<tFile | null>(null);
  const [photosToPrint, setPhotosToPrint] = useState<string[]>([]);
  const { source: sourceSegment, id: reportId = null, equipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const reportReturnState = (location.state || null) as
    | { returnTo?: string; returnLabel?: string }
    | null;
  const [report, setReport] = useState<tReportData | undefined | null>(reportId && reportFromLS ? reportFromLS[reportId] : null);
  const [machineHours, setMachineHours] = useState<number | undefined>()
  const [reportUmLabel, setReportUmLabel] = useState('UM')
  const [lastServiceHours, setLastServiceHours] = useState<number | undefined>()
  const [loc, setLoc] = useState(report ? JSON.parse(report?.userLocation) : {})
  const [issues, setIssues] = useState<tIssuesReport[]>([]);
  const [isLoadingIssues, setIsLoadingIssues] = useState<boolean>(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [equipmentLocation, setEquipmentLocation] = useState<string | null>(null)
  const [equipmentDistrict, setEquipmentDistrict] = useState<string | null>(null)
  const [equipmentRegion, setEquipmentRegion] = useState<string | null>(null)
  const [isDailyServiceLog, setIsDailyServiceLog] = useState<boolean>(false)
  const [isShiftReport, setIsShiftReport] = useState<boolean>(false)
  const [selectedLog, setSelectedLog] = useState<tIssueLog[] | null>(null)
  const [shouldPrintReport, setShouldPrintReport] = useState(false);
  const { company: userContextCompany } = useUserContext()
  const { companyGroupedEquipmentData } = useEquipmentContext()
  const { companyRegions, companyLocations, companyDistricts } = useRegionAndLocationContext()
  const { isSideNavOpen, setIsSideNavOpen, initialSideNavOpenState, setInitialSideNavOpenState } = useAppContext();

  // you can use a function to return the target element besides using React refs
  const getTargetElement = () => document.getElementById("content-id"); // Used for PDF generation

  useEffect(() => {
    if (!reportId) {
      setReport(null);
      setSignature(null);
      return;
    }

    let cachedReport: tReportData | null = null;
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.REPORT);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, tReportData>;
        cachedReport = parsed[reportId] ?? null;
      }
    } catch {
      cachedReport = null;
    }

    setReport(cachedReport);
    setSignature(null);
    setIssues([]);
    setIsLoadingIssues(false);

    void getReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, sourceSegment]);

  useEffect(() => {
    if (shouldPrintReport) {
      window.print();
      setShouldPrintReport(false);
      setIsSideNavOpen(initialSideNavOpenState)
    }
  }, [shouldPrintReport])

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      const signaturePath = report?.signature;
      if (!signaturePath) {
        if (!isCancelled) {
          setSignature(null);
        }
        return;
      }

      try {
        const imageUrl = await resolveStorageImageUrl(signaturePath);
        if (!isCancelled) {
          setSignature(imageUrl);
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        if (!isCancelled) {
          setSignature(null);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [report?.id, report?.signature]);

  useEffect(() => {
    if (report) {
      const items: tItems[] = report?.userData ? JSON.parse(report.userData) : [];
      const hasIssue = items?.some((item: { value: string; }) => item.value === ITEM_STATUS_CODES.inop || item.value === ITEM_STATUS_CODES.warn);
      if (hasIssue) {
        getIssues();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const getReport = async () => {
    if (!reportId) {
      return;
    }
    try {
      const source = sourceSegment
        ? reportPathSegmentToSource(sourceSegment)
        : searchParams.get('source') || '';
      let reportData: any = null;

      if (source === 'InspectionReport') {
        // Query the new InspectionReport table
        const res = await authClient.graphql({
          query: getInspectionReportQuery,
          variables: { id: reportId },
        });
        const ir = res.data.getInspectionReport;
        if (ir) {
          // Map InspectionReport fields to the legacy Report shape the component expects
          reportData = {
            id: ir.id,
            companyId: ir.companyId,
            equipmentId: ir.equipmentCategoryId,
            equipmentName: ir.equipmentCategoryName,
            unitId: ir.unitId,
            unitName: ir.unitName,
            userId: ir.userId,
            name: ir.userName,
            userData: ir.responseData, // AWSJSON → same shape as legacy userData
            userLocation: JSON.stringify({
              type: ir.gpsLocationType,
              latitude: ir.gpsLatitude,
              longitude: ir.gpsLongitude,
            }),
            signature: ir.signature,
            inspectionStarted: ir.inspectionStarted,
            inspectionCompleted: ir.inspectionCompleted,
            createdAt: ir.createdAt,
            updatedAt: ir.updatedAt,
            umAtReport: resolveUMCounterFromInspectionReport(ir) ?? null,
            dailyServiceLogReport: false,
            alwaysTriggerEmail: ir.alwaysTriggerEmail,
            equipmentLocationId: ir.locationId,
          };
        }
      } else if (source === 'DailyServiceReport') {
        // Query the new DailyServiceReport table
        const res = await authClient.graphql({
          query: getDailyServiceReportQuery,
          variables: { id: reportId },
        });
        const dsr = res.data.getDailyServiceReport;
        if (dsr) {
          reportData = {
            id: dsr.id,
            companyId: dsr.companyId,
            equipmentId: dsr.equipmentCategoryId,
            equipmentName: dsr.equipmentCategoryName,
            unitId: dsr.unitId,
            unitName: dsr.unitName,
            userId: dsr.userId,
            name: dsr.userName,
            userData: dsr.responseData,
            userLocation: JSON.stringify({
              type: dsr.gpsLocationType,
              latitude: dsr.gpsLatitude,
              longitude: dsr.gpsLongitude,
            }),
            signature: dsr.signature,
            inspectionStarted: dsr.inspectionStarted,
            inspectionCompleted: dsr.inspectionCompleted,
            createdAt: dsr.createdAt,
            updatedAt: dsr.updatedAt,
            umAtReport: resolveUMCounterFromDailyServiceReport(dsr) ?? null,
            dailyServiceLogReport: true,
            equipmentLocationId: dsr.locationId,
          };
        }
      } else if (source === 'ShiftReport') {
        // Query the new ShiftReport table
        const res = await authClient.graphql({
          query: getShiftReportQuery,
          variables: { id: reportId },
        });
        const sr = (res.data as any).getShiftReport;
        if (sr) {
          const shiftLabel = sr.shiftType === 'end-of-day' ? 'End of Day' : 'Start of Day';
          reportData = {
            id: sr.id,
            companyId: sr.companyId,
            equipmentId: '',
            equipmentName: `${shiftLabel} Report`,
            unitId: '',
            unitName: '',
            userId: sr.userId,
            name: sr.userName,
            userData: sr.responseData,
            userLocation: JSON.stringify({
              type: sr.gpsLocationType,
              latitude: sr.gpsLatitude,
              longitude: sr.gpsLongitude,
            }),
            signature: sr.signature,
            inspectionStarted: sr.inspectionStarted,
            inspectionCompleted: sr.inspectionCompleted,
            createdAt: sr.createdAt,
            updatedAt: sr.updatedAt,
            dailyServiceLogReport: false,
            equipmentLocationId: sr.locationId,
          };
        }
      } else {
        // Default: query the legacy Report table
        const res = await authClient.graphql({
          query: getReportQuery,
          variables: { id: reportId },
        });
        reportData = res.data.getReport;
      }

      if (!reportData) {
        // If source-specific query returned null, try legacy as fallback
        if (source && source !== 'LegacyReport') {
          const fallbackRes = await authClient.graphql({
            query: getReportQuery,
            variables: { id: reportId },
          });
          reportData = fallbackRes.data.getReport;
        }
        if (!reportData) return;
      }

      if (reportData?.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID) {
        reportData.equipmentName = KEYS_START_OF_DAY_REPORT.EQUIPMENT_NAME;
        setIsDailyServiceLog(false);
        setIsShiftReport(false);

        if (reportData?.userLocation) {
          setLoc(JSON.parse(reportData.userLocation));
        }

        setReport(reportData);
      } else {
        getEquipmentInfo(reportData?.equipmentId as string, reportData?.unitId as string | undefined);
        getLocationOfEquipment(reportData?.equipmentId as string, reportData);
        setIsDailyServiceLog(!!reportData?.dailyServiceLogReport);
        setIsShiftReport(source === 'ShiftReport');

        if (reportData?.userLocation) {
          setLoc(JSON.parse(reportData.userLocation));
        }

        setReport(reportData);
      }
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
    }
  };

  const getEquipmentInfo = async (id: string, unitId?: string) => {
    const equipment = companyGroupedEquipmentData.find(eq => eq.id === id);
    if (!unitId || !equipment?.units?.length) return;

    const currentUnit = equipment.units.find((unit) => unit?.id === unitId);
    if (!currentUnit) return;

    setReportUmLabel(displayUnitOfMeasure(currentUnit.unitOfMeasure) || 'UM');
    setMachineHours(currentUnit.currentUMCounterValue || currentUnit.currentMachineHours || 0);
    setLastServiceHours(currentUnit.lastServiceHours === null ? 0 : currentUnit.lastServiceHours);
  }

  const getLocationOfEquipment = async (id: string, reportData?: any) => {
    try {

      const equipment = companyGroupedEquipmentData.find(eq => eq.id === id);

      if (!equipment) {
        setEquipmentLocation("Unassigned");
        setEquipmentDistrict(null);
        setEquipmentRegion("Unassigned");
        return;
      }
      // const equipment = equipmentResult.data.getEquipment;
      // let locationId = equipment?.units?.find(unit => unit?.id === (reportData?.unitId || report?.unitId))?.locationId

      const unitId = reportData?.unitId || report?.unitId;
      const unit = equipment.units?.find(unit => unit?.id === unitId);
      const locationId = unit?.locationId;

      if (locationId) {
        // Find location from context instead of API call
        const location: (typeof companyLocations)[number] & { districtId?: string | null } | undefined =
          companyLocations.find(loc => loc.id === locationId);

        if (location) {
          setEquipmentLocation(location.name || "Unassigned");

          // Find district from context
          if (location.districtId) {
            const district = companyDistricts.find(d => d.id === location.districtId);
            setEquipmentDistrict(district?.name || null);
          } else {
            setEquipmentDistrict(null);
          }

          // Find region from context instead of API call
          if (location.regionalDivisionId) {
            const region = companyRegions.find(reg => reg.id === location.regionalDivisionId);
            setEquipmentRegion(region?.name || "Unassigned");
          } else {
            setEquipmentRegion("Unassigned");
          }
        } else {
          setEquipmentLocation("Unassigned");
          setEquipmentDistrict(null);
          setEquipmentRegion("Unassigned");
        }
      } else {
        setEquipmentLocation("Unassigned");
        setEquipmentDistrict(null);
        setEquipmentRegion("Unassigned");
      }
    } catch (error) {
      console.error('Error fetching location data:', error);
      Sentry.captureException(error);
    }
  };

  const getIssues = async () => {
    if (!report) return;
    try {
      setIsLoadingIssues(true);
      const sortOrder = {
        [ITEM_STATUS_CODES.na]: 4,
        [ITEM_STATUS_CODES.ok]: 3,
        [ITEM_STATUS_CODES.warn]: 2,
        [ITEM_STATUS_CODES.inop]: 1
      }
      const hasUnitId = report.unitId;
      if (hasUnitId) {
        const res = await authClient.graphql({
          query: discrepanciesByUnitIdQuery,
          variables: { unitId: report.unitId as string }
        });
        const newIssues: tIssuesReport[] = []
        res.data.listDiscrepancyByUnitIdAndUpdatedAt.items.sort((a, b) => (a.createdAt ?? '') > (b.createdAt ?? '') ? -1 : 1).forEach((item) => {
          const logInResponse = JSON.parse(item.log as string) as tIssueLog[]
          newIssues.push(
            {
              questionIndex: item.questionIndex,
              initialStatus: item.initialStatus || '',
              log: logInResponse,
              status: item.status,
            }
          )
        });
        setIssues(newIssues.sort((a, b) => {
          return sortOrder[a.status] - sortOrder[b.status];
        }));
      } else {
        const res = await authClient.graphql({
          query: discrepanciesByEquipmentIdQuery,
          variables: { equipmentId: report.equipmentId as string }
        })
        const newIssues: tIssuesReport[] = []
        res.data.listDiscrepancyByEquipmentIdAndUpdatedAt.items.sort((a, b) => (a.createdAt ?? '') > (b.createdAt ?? '') ? -1 : 1).forEach((item) => {
          const logInResponse = JSON.parse(item.log as string) as tIssueLog[]
          newIssues.push(
            {
              questionIndex: item.questionIndex,
              initialStatus: item.initialStatus || '',
              log: logInResponse,
              status: item.status,
            }
          )
        });
        setIssues(newIssues.sort((a, b) => {
          return sortOrder[a.status] - sortOrder[b.status];
        }));
      }
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
    } finally {
      setIsLoadingIssues(false);
    }
  }

  useEffect(() => {
    const getListOfPhotosToRenderForPrint = async () => {
      if (!report?.userData) return;
      const newPhotos = [];
      const userdata = JSON.parse(report.userData);
      try {
        for (const el of userdata) {
          if (el.photos) {
            for (const photo of el.photos) {
              let photoUrl;

              if (photo.split("/")[0] === "public") {
                photoUrl = await getUrl({
                  path: photo,
                });
              } else {
                photoUrl = await getUrl({
                  path: `public/${photo}`,
                });
              }
              const path = photoUrl.url.pathname;
              const url = photoUrl.url.href;
              if (
                path.includes(".png") ||
                path.includes(".jpeg") ||
                path.includes(".jpg")
              ) {
                newPhotos.push(url);
              }
            }
          }
        }
        setPhotosToPrint([...newPhotos]);
      } catch (error) {
        console.log(error);
        Sentry.captureException(error);
      }
    };
    getListOfPhotosToRenderForPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const getFileTypeFromFilename = (fileName: string) => {
    const type = getTypeFromFileName(fileName);
    switch (type) {
      case "image":
        return <i className="fa-solid fa-image" />;
      case "video":
        return <i className="fa-solid fa-video" />;
      case "audio":
        return <i className="fa-solid fa-microphone-lines" />;
      default:
        return fileName;
    }
  };

  const renderMediaFile = (fileName: tFile | null) => {
    const mediaFileUrl = fileName?.url?.href;
    if (!mediaFileUrl) return null;
    const type = getTypeFromFileName(mediaFileUrl);
    switch (type) {
      case "image":
      default:
        return <img src={mediaFileUrl} alt="detail" width="100%" />;
      case "video":
        return <video src={mediaFileUrl} controls width="100%" />;
      case "audio":
        return <audio src={mediaFileUrl} controls />;
    }
  };

  const items: tItems[] = report?.userData ? JSON.parse(report.userData) : [];
  const useFlowReportLayout = !isShiftReport;
  const reportCurrentUm =
    report?.umAtReport != null && Number.isFinite(Number(report.umAtReport))
      ? Number(report.umAtReport)
      : null;
  const storedLastServiceUm =
    lastServiceHours != null && isNumeric(lastServiceHours) ? Number(lastServiceHours) : null;
  const reportLastServiceUm =
    reportCurrentUm != null && storedLastServiceUm != null && storedLastServiceUm < reportCurrentUm
      ? storedLastServiceUm
      : null;
  const reportUmSinceLastService =
    reportCurrentUm != null && reportLastServiceUm != null
      ? reportCurrentUm - reportLastServiceUm
      : null;

  const handleBackToEquipment = () => {
    if (reportReturnState?.returnTo) {
      navigate(reportReturnState.returnTo);
      return;
    }

    if (report?.unitId) {
      navigate(`/equipment/${report.unitId}`);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/equipment');
  };

  const backButtonText = reportReturnState?.returnLabel || 'Back to Equipment';

  const handleOpenReportMedia = async (mediaPath: string) => {
    try {
      let photoUrl;
      if (mediaPath.split("/")[0] === "public") {
        photoUrl = await getUrl({ path: mediaPath }) as tFile;
      } else {
        photoUrl = await getUrl({ path: `public/${mediaPath}` }) as tFile;
      }
      setRenderPhotosModal(photoUrl);
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  const flowReportTitle = report?.unitName
    ? `${report.unitName}`
    : report?.equipmentName || 'Report';

  const mostSevereStatus = () => {
    const hasInOp = items.some((item: { value: string; }) => item.value === ITEM_STATUS_CODES.inop);
    const hasWarn = items.some((item: { value: string; }) => item.value === ITEM_STATUS_CODES.warn);
    const hasUnderReview = items.some((item: { value: string; }) => item.value === ITEM_STATUS_CODES.under_review);
    switch (true) {
      case hasInOp:
        return <StatusIndicator type="error">Out Of Service</StatusIndicator>;
      case hasWarn:
        return (<>
          <Icon url="/status-warning-svgrepo-com.svg" size="medium" variant="normal" /> <span style={{
            color: "#ebe424", fontWeight: "bold", fontSize: "medium"
          }}>Warning</span></>);
      case hasUnderReview:
        return <StatusIndicator type="in-progress">Under Review</StatusIndicator>;
      default:
        return <StatusIndicator type="success">OK</StatusIndicator>;
    }
  };

  const mostSevereCurrentStatus = () => {
    switch (true) {
      case issues[0].status === ITEM_STATUS_CODES.inop:
        return <StatusIndicator type="error">Out Of Service</StatusIndicator>;
      case issues[0].status === ITEM_STATUS_CODES.warn:
        return (<>
          <Icon url="/status-warning-svgrepo-com.svg" size="medium" variant="normal" /> <span style={{ color: "#ebe424", fontWeight: "bold" }}>Warning</span></>);
      case issues[0].status === ITEM_STATUS_CODES.under_review:
        return <StatusIndicator type="in-progress"> <span style={{ color: "blue", fontWeight: "bold" }}>Under Review</span></StatusIndicator>;
      default:
        return <StatusIndicator type="success">OK</StatusIndicator>;
    }
  };

  return (
    <div className={`page-container${useFlowReportLayout ? ' inspection-flow-page' : ''}`}>
    <div id="content-id">
      <div className="viewAlwaysExceptPrint">
        {useFlowReportLayout ? (
          <InspectionFlowReportView
            reportTitle={flowReportTitle}
            reportKindLabel={isDailyServiceLog ? 'Daily Service Report' : 'Inspection Report'}
            regionLabel={equipmentRegion || 'Unassigned'}
            locationLabel={equipmentLocation || 'Unassigned'}
            isDailyServiceLog={isDailyServiceLog}
            submittedBy={report?.name}
            inspectionStarted={report?.inspectionStarted}
            inspectionCompleted={report?.inspectionCompleted}
            createdAt={report?.createdAt}
            currentUm={reportCurrentUm}
            lastServiceUm={reportLastServiceUm}
            umSinceLastService={reportUmSinceLastService}
            umLabel={reportUmLabel}
            signatureUrl={signature}
            locationType={loc?.type}
            mapCoordinates={
              loc?.latitude && loc?.longitude
                ? { latitude: loc.latitude, longitude: loc.longitude }
                : null
            }
            mapPopupTitle={`${report?.equipmentName ?? ''}${report?.unitName ? ` / ${report.unitName}` : ''}`.trim()}
            mapPopupSubtitle={report?.name ?? undefined}
            items={items}
            onBack={handleBackToEquipment}
            backButtonText={backButtonText}
            onPrint={() => {
              setInitialSideNavOpenState(isSideNavOpen);
              setIsSideNavOpen(false);
              setShouldPrintReport(true);
            }}
            onDownloadPdf={() =>
              generatePDF(getTargetElement, {
                filename: [
                  report?.equipmentName,
                  report?.unitName,
                  report?.createdAt ? new Date(report.createdAt).toISOString().slice(0, 10) : null,
                ]
                  .filter(Boolean)
                  .join('-'),
                ...PDF_OPTIONS,
              } as Options)
            }
            onOpenMedia={handleOpenReportMedia}
          />
        ) : (
        <Stack direction="vertical" size="m">
          <>
            <PageHeader
              as="h1"
              title={
                isShiftReport
                  ? (report?.equipmentName ?? 'Shift Report')
                  : `${report?.equipmentName ?? ''}${report?.unitName ? ` / ${report.unitName}` : ''} - ${isDailyServiceLog ? 'Daily Service Log' : 'Inspection Report'}`
              }
              subtitle={`Region: ${equipmentRegion || 'Unassigned'}`}
              signalLine={`Location: ${equipmentLocation || 'Unassigned'}`}
              actions={
                <>
                  <BackButton buttonClasses="back-button--inline" />
                  <Btn
                    variant="secondary"
                    buttonSize="sm"
                    buttonIcon={<IconPrint />}
                    ariaLabel="Print report"
                    buttonOnClick={() => {
                      setInitialSideNavOpenState(isSideNavOpen);
                      setIsSideNavOpen(false);
                      setShouldPrintReport(true);
                    }}
                  />
                  <Btn
                    variant="secondary"
                    buttonSize="sm"
                    buttonIcon={<IconDownload />}
                    ariaLabel="Download PDF"
                    buttonOnClick={() =>
                      generatePDF(getTargetElement, {
                        filename: [
                          report?.equipmentName,
                          report?.unitName,
                          report?.createdAt ? new Date(report.createdAt).toISOString().slice(0, 10) : null,
                        ]
                          .filter(Boolean)
                          .join('-'),
                        ...PDF_OPTIONS,
                      } as Options)
                    }
                  />
                </>
              }
            />

            {equipmentId && (
              <Button
                variant="inline-link"
                onClick={() => {
                  window.close();
                }}
              >
                Close
              </Button>
            )}
            <div >
              <Container>
                <ColumnLayout columns={3} variant="text-grid">
                  <Stack size="m">
                    {report?.inspectionStarted ? (
                      <>
                        <ValueWithLabel label={`${isDailyServiceLog ? "Daily Service Started: " : "Inspection Started: "}`}>
                          {new Date(report?.inspectionStarted || Date.now()).toLocaleString()}
                        </ValueWithLabel>
                        <ValueWithLabel label={`${isDailyServiceLog ? "Daily Service Completed: " : "Inspection Completed: "}`}>
                          {new Date(report?.inspectionCompleted || Date.now()).toLocaleString()}
                        </ValueWithLabel>
                      </>
                    ) : <>
                      <ValueWithLabel label={`${isDailyServiceLog ? "Daily Service Completed: " : "Inspection Completed: "}`}>
                        {new Date(report?.createdAt || Date.now()).toLocaleString()}
                      </ValueWithLabel>
                    </>}
                    <ValueWithLabel label="Submitted By">
                      {report?.name}
                    </ValueWithLabel>
                    {signature ? <ValueWithLabel label="Signature:">
                      <img style={{ display: 'block', maxHeight: '60px', width: 'auto', objectFit: 'contain' }} src={signature} alt="Signature" crossOrigin="anonymous" />
                    </ValueWithLabel> : <></>}
                  </Stack>
                  <Stack size="m">
                    <ValueWithLabel label={`${reportUmLabel} at this report`}>
                      {report?.umAtReport != null && Number.isFinite(report.umAtReport)
                        ? Number(report.umAtReport).toLocaleString()
                        : '--'}
                    </ValueWithLabel>
                    <ValueWithLabel label={DEFAULT_REPORT_LABELS.lastServiceUM}>
                      {lastServiceHours ? lastServiceHours : 0}
                    </ValueWithLabel>
                    <ValueWithLabel
                      label={DEFAULT_REPORT_LABELS.UMSinceLastService}
                    >
                      {isNumeric(machineHours) && isNumeric(lastServiceHours)
                        ? (
                          Number(machineHours) - Number(lastServiceHours)
                        ).toLocaleString()
                        : "--"}
                    </ValueWithLabel>
                  </Stack>
                  <Stack size="m">
                    <ValueWithLabel label={issues.length > 0 && issues[0].status !== issues[0].initialStatus ? 'Status Submitted / Current Status' : 'Status'}>
                      {issues.length > 0 && issues[0].status !== issues[0].initialStatus ? <>{mostSevereStatus()} / {mostSevereCurrentStatus()}</> : mostSevereStatus()}
                    </ValueWithLabel>
                    <ValueWithLabel label="Location Source">
                      {loc?.type || "--"}
                    </ValueWithLabel>
                  </Stack>
                </ColumnLayout>
              </Container>
            </div>
            {isLoadingIssues || issues.length > 0 &&
              <>
                <div className="hide-from-printer">
                  <Table
                    header={<Header variant="h2">Issues Log</Header>}
                    wrapLines
                    trackBy="id"
                    stripedRows
                    items={issues}

                    columnDefinitions={[
                      {
                        id: "sectionMobile",
                        header: "Section",
                        cell: (item) => {
                          const prompts: tItems[] = report?.userData ? JSON.parse(report.userData) : [];
                          const section = item.questionIndex ? prompts[item.questionIndex] : null;
                          // return section ? `${section.title} - ${section.label}` : ''
                          return (
                            <>
                              <Box variant="span" fontWeight="bold">
                                {section ? section.label : ''}
                              </Box>
                              {section?.title ? <br /> : ""}
                              <Box variant="small" fontWeight="heavy">
                                {section ? section.title : ''}
                              </Box>
                              {section?.label ? <br /> : ""}
                              <Box variant="small">
                                {section ? section.description : ""}
                              </Box>
                            </>
                          )
                        }
                      },
                      {
                        id: "section",
                        header: "Section",
                        cell: (item) => {
                          const prompts: tItems[] = report?.userData ? JSON.parse(report.userData) : [];
                          const section = item.questionIndex ? prompts[item.questionIndex] : null;
                          return section ? `${section.title} - ${section.label}` : ''
                        }
                      },
                      {
                        id: "status",
                        header: "Current Status",
                        cell: (item) => <DisplayStatusIcon size="medium">{item.status}</DisplayStatusIcon>,
                      },
                      {
                        id: "statusMobile",
                        header: "Current Status",
                        cell: (item) => <DisplayStatusIcon size="small">{item.status}</DisplayStatusIcon>,
                      },
                      {
                        id: "initialStatusMobile",
                        header: "Initial Status",
                        cell: (item) => <DisplayStatusIcon size="small">{item.initialStatus}</DisplayStatusIcon>,
                      },
                      {
                        id: "initialStatus",
                        header: "Initial Status",
                        cell: (item) => <DisplayStatusIcon size="medium">{item.initialStatus}</DisplayStatusIcon>,
                      },
                      {
                        id: "log",
                        header: 'Log',
                        cell: (item) => (
                          <Button
                            variant="inline-link"
                            onClick={() => {
                              setSelectedLog(item.log);
                            }}
                          >
                            <i className="fa-regular fa-file-lines fa-icon-black"></i>
                          </Button>
                        ),
                      },
                    ]}
                    columnDisplay={[
                      { id: 'sectionMobile', visible: (window.innerWidth <= 576) },
                      { id: 'section', visible: (window.innerWidth > 577) },

                      { id: 'initialStatus', visible: (window.innerWidth > 577) },
                      { id: 'initialStatusMobile', visible: (window.innerWidth <= 576) },
                      { id: 'status', visible: (window.innerWidth > 577) },
                      { id: 'statusMobile', visible: (window.innerWidth <= 576) },
                      { id: 'log', visible: true },
                    ]}
                    loading={isLoadingIssues} />
                </div>
              </>
            }
            {loc?.latitude && loc?.longitude && (
              <>
                <div className="hide-from-printer">
                  <Header variant="h2">Map</Header>

                </div>
                <div className="hide-from-printer">
                  <div style={{ maxWidth: "100%", margin: "0 auto" }}>
                    <MapContainer
                      id="map"
                      center={[loc.latitude, loc.longitude]}
                      zoom={13}
                      scrollWheelZoom={false}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[loc.latitude, loc.longitude]}>
                        <Popup>
                          <Box>{`${report?.equipmentName} ${report?.unitName ? `/ ${report?.unitName}` : ""
                            }`}</Box>
                          <Box>{report?.name}</Box>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                </div>
              </>
            )}
          </>
          <div className="hide-from-printer">
            <Table
              stripedRows
              header={<Header variant="h2">{isDailyServiceLog ? "Daily Service Details: " : "Inspection Details: "}</Header>}
              // variant="embedded"
              columnDefinitions={[
                {
                  id: "promptMobile",
                  header: "Component",
                  cell: (item) => (
                    <>
                      <Box variant="span" fontWeight="bold">
                        {item.label}
                      </Box>
                      {item.title ? <br /> : ""}
                      <Box variant="small" fontWeight="heavy">
                        {item.type === DEFAULT_FORM_INPUT_TYPES.section
                          ? item.title
                          : ""}
                      </Box>
                      {item.description ? <br /> : ""}
                      <Box variant="small">
                        {item.description}
                      </Box>
                    </>

                  ),
                },
                {
                  id: "prompt",
                  header: "Prompt",
                  cell: (item) => item.label,
                },
                {
                  id: "section",
                  header: "Section",
                  cell: (item) =>
                    item.type === DEFAULT_FORM_INPUT_TYPES.section
                      ? item.title
                      : "",
                },
                {
                  id: "description",
                  header: "Description",
                  cell: (item) => item.description,
                },
                {
                  id: "valueMobile",
                  header: "Value",
                  cell: (item) => {
                    // For checkbox questions, show selected options
                    if ('selectedOptions' in item && item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions) {
                      return (
                        <div>
                          {item.selectedOptions.map((option, index) => (
                            <div key={index}>
                              <DisplayStatusIcon size="big">{option}</DisplayStatusIcon>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    // For other question types, show the value
                    return (
                      <DisplayStatusIcon size="medium">
                        {isNumeric(item.value)
                          ? Number(item.value).toLocaleString()
                          : item.value}
                      </DisplayStatusIcon>
                    );
                  },
                },
                {
                  id: "value",
                  header: "Value",
                  cell: (item) => {
                    // For checkbox questions, show selected options
                    if ('selectedOptions' in item && item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions) {
                      return (
                        <div>
                          {item.selectedOptions.map((option, index) => (
                            <div key={index}>
                              <DisplayStatusIcon size="big">{option}</DisplayStatusIcon>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    // For other question types, show the value
                    return (
                      <DisplayStatusIcon size="medium">
                        {isNumeric(item.value)
                          ? Number(item.value).toLocaleString()
                          : item.value}
                      </DisplayStatusIcon>
                    );
                  },
                },
                {
                  id: "notes",
                  header: "Notes",
                  cell: (item) => item.notes,
                },
                {
                  id: "media",
                  header: "Media",
                  cell: (item) =>
                    item.photos?.length ? (
                      <div className="hide-from-printer">
                        <Stack direction="horizontal" size="s">
                          {item.photos.map((media) => {
                            return (
                              <Button
                                variant="inline-link"
                                key={media}
                                onClick={async () => {
                                  let photoUrl;
                                  if (media.split("/")[0] === "public") {
                                    photoUrl = await getUrl({
                                      path: media,
                                    }) as tFile;
                                  } else {
                                    photoUrl = await getUrl({
                                      path: `public/${media}`,
                                    }) as tFile;
                                  }
                                  setRenderPhotosModal(photoUrl);
                                }}
                              >
                                {getFileTypeFromFilename(media)}
                              </Button>
                            );
                          })}
                        </Stack>
                      </div>
                    ) : (
                      "--"
                    ),
                },
              ]}
              columnDisplay={[
                { id: 'promptMobile', visible: (window.innerWidth <= 576) },
                { id: 'prompt', visible: (window.innerWidth > 577) },
                { id: 'section', visible: (window.innerWidth > 577) },
                { id: 'description', visible: (window.innerWidth > 577) },
                { id: 'value', visible: (window.innerWidth > 577) },
                { id: 'valueMobile', visible: (window.innerWidth <= 576) },
                { id: 'notes', visible: true },
                { id: 'media', visible: true },
              ]}
              items={items}
              trackBy="id"
              wrapLines
            />
          </div>

        </Stack>
        )}
      </div>

      <div className="viewPrint">
        <Stack size="xs">
          <>
            {equipmentId && (
              <Button
                variant="inline-link"
                onClick={() => {
                  window.close();
                }}
              >
                Close
              </Button>
            )}
            <Box variant="h3">{`${userContextCompany?.name} - ${isDailyServiceLog ? "Daily Service Log" : "Equipment Inspection Report"}`}</Box>

            <Box variant="h5">{`Equipment: ${report?.equipmentName}`}</Box>
            <Box >
              {equipmentRegion && `Region: ${equipmentRegion}`}
            </Box>
            {equipmentDistrict && <Box>
              {`District: ${equipmentDistrict}`}
            </Box>}
            <Box >
              {equipmentLocation && ` Location: ${equipmentLocation}`}
            </Box>
            <hr />
            <ColumnLayout columns={12} variant="text-grid">
              <Stack size="xs">
                {report?.inspectionStarted ? (
                  <>
                    <ValueWithLabel label={`${isDailyServiceLog ? "Daily Service Started: " : "Inspection Started: "}`}>
                      {new Date(report?.inspectionStarted || Date.now()).toLocaleString()}
                    </ValueWithLabel>
                    <ValueWithLabel label={`${isDailyServiceLog ? "Daily Service Completed: " : "Inspection Completed: "}`}>
                      {new Date(report?.inspectionCompleted || Date.now()).toLocaleString()}
                    </ValueWithLabel>
                  </>
                ) : <>
                  <ValueWithLabel label="Inspection Completed:">
                    {new Date(report?.createdAt || Date.now()).toLocaleString()}
                  </ValueWithLabel>
                </>}
              </Stack>
              <Stack size="xs">
                <ValueWithLabel label="Submitted By">
                  {report?.name}
                </ValueWithLabel>
                {signature ? <ValueWithLabel label="Signature:">
                  <img style={{ display: 'block', maxHeight: '60px', width: 'auto', objectFit: 'contain' }} src={signature} alt="Signature" />
                </ValueWithLabel> : <></>}
              </Stack>
              <Stack size="xs">
                <ValueWithLabel label={`${reportUmLabel} at this report`}>
                  {report?.umAtReport != null && Number.isFinite(report.umAtReport)
                    ? Number(report.umAtReport).toLocaleString()
                    : '--'}
                </ValueWithLabel>
                <ValueWithLabel label={DEFAULT_REPORT_LABELS.lastServiceHours}>
                  {lastServiceHours ? lastServiceHours : 0}
                </ValueWithLabel>
                <ValueWithLabel
                  label={DEFAULT_REPORT_LABELS.timeSinceLastService}
                >
                  {isNumeric(machineHours) && isNumeric(lastServiceHours)
                    ? (
                      Number(machineHours) - Number(lastServiceHours)
                    ).toLocaleString()
                    : "--"}
                </ValueWithLabel>
              </Stack>
              <Stack size="xs">
                <ValueWithLabel label={issues.length > 0 && issues[0].status !== issues[0].initialStatus ? 'Status Submitted / Current Status' : 'Status'}>
                  {issues.length > 0 && issues[0].status !== issues[0].initialStatus ? <>{mostSevereStatus()} / {mostSevereCurrentStatus()}</> : mostSevereStatus()}
                </ValueWithLabel>
                <ValueWithLabel label="Location Source">
                  {loc?.type || "--"}
                </ValueWithLabel>
              </Stack>
            </ColumnLayout>
          </>
          <hr />
          <div className="viewInPrint">
            <Table
              variant="embedded"
              stripedRows
              columnDefinitions={[
                {
                  id: "prompt",
                  header: "Component",
                  cell: (item) => item.label
                },
                {
                  id: "value",
                  header: "Value",
                  cell: (item) => {
                    // For checkbox questions, show selected options
                    if ('selectedOptions' in item && item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions) {
                      return (
                        <div>
                          {item.selectedOptions.map((option, index) => (
                            <div key={index}>
                              <DisplayStatusIcon size="big">{option}</DisplayStatusIcon>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    // For other question types, show the value
                    return (
                      <DisplayStatusIcon size="medium">
                        {isNumeric(item.value)
                          ? Number(item.value).toLocaleString()
                          : item.value}
                      </DisplayStatusIcon>
                    );
                  },
                },
                {
                  id: "notes",
                  header: "Notes",
                  cell: (item) => item.notes,
                },
                {
                  id: "media",
                  header: "Media",
                  cell: (item) =>
                    item.photos?.length ? (
                      <Button variant="inline-link">
                        {getFileTypeFromFilename("photo")}
                      </Button>

                    ) : (
                      "--"
                    ),
                },
              ]}
              items={items}
              trackBy="id"
              wrapLines
            />

          </div>
        </Stack>
        {photosToPrint.length > 0 && <div className="printer-only-media">
          <Box variant="h2">Images</Box>
          {photosToPrint.map((photoUrl, idx) => {
            return (
              <span key={idx} className="upload-media">
                <img src={photoUrl} alt="detail" width="100%" />
              </span>
            );
          })}
        </div>}
      </div>

      <Modal
        onDismiss={() => setRenderPhotosModal(null)}
        visible={!!renderPhotosModal}
        header="Inspection Media"
        footer={
          <Box float="right">
            <Stack direction="horizontal" size="xs">
              <Button
                variant="primary"
                onClick={() => setRenderPhotosModal(null)}
              >
                Close
              </Button>
            </Stack>
          </Box>
        }
      >
        {renderMediaFile(renderPhotosModal)}
      </Modal>

      {/* modal for showing issue history */}
      <Modal visible={selectedLog !== null} onDismiss={() => {
        setSelectedLog(null)
      }}>

        <Box padding="l">
          <Stack size="l">
            <Header variant="h2">Issue History</Header>
            {selectedLog?.map((logEntry: tIssueLog, idx: number) => {
              const entryDate = new Date(logEntry.date);

              return (

                <Box
                  key={idx}
                  margin={{ bottom: 'l', top: 'l' }}
                  padding="l"
                >
                  <Stack size="s">
                    <Box variant="p">
                      {logEntry.notes}
                    </Box>
                    <Box variant="span" fontWeight="bold">
                      {logEntry.name}
                    </Box>
                    <Box variant="small">
                      {`${entryDate.toLocaleDateString()} ${entryDate.toLocaleTimeString()}`}
                    </Box>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        </Box>
      </Modal>
    </div>
    </div>
  );
};
